import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { tradersService } from '@/services/traders/traders.service';

type FilterStatus = 'all' | 'active' | 'expired' | 'revoked';

type DeviceSession = {
  id: string;
  status: Exclude<FilterStatus, 'all'>;
  username: string;
  email?: string;
  totp_enabled: boolean;
  browser: string;
  os: string;
  user_agent: string;
  last_ip?: string;
  location?: string;
  created_at: string;
  last_seen_at: string;
  trusted_until: string;
  revoked_at?: string;
};

type SessionsSummary = {
  total_users: number;
  totp_enabled: number;
  totp_disabled: number;
  users_with_devices: number;
  active_devices: number;
  devices_history: number;
};

const emptySummary: SessionsSummary = {
  total_users: 0,
  totp_enabled: 0,
  totp_disabled: 0,
  users_with_devices: 0,
  active_devices: 0,
  devices_history: 0,
};

const labels = {
  active: 'Активна',
  expired: 'Истекла',
  revoked: 'Отозвана',
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString('ru-RU') : '—';

const summaryCards: Array<{ key: keyof SessionsSummary; label: string }> = [
  { key: 'total_users', label: 'Пользователей' },
  { key: 'totp_enabled', label: 'С включённой 2FA' },
  { key: 'totp_disabled', label: 'Без 2FA' },
  { key: 'users_with_devices', label: 'С активными устройствами' },
  { key: 'active_devices', label: 'Активных устройств' },
  { key: 'devices_history', label: 'Всего устройств в истории' },
];

export default function TraderDevices() {
  const [items, setItems] = useState<DeviceSession[]>([]);
  const [summary, setSummary] = useState<SessionsSummary>(emptySummary);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<FilterStatus>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [sessionsData, summaryData] = await Promise.all([
        tradersService.getAuthSessions({
          search: search.trim() || undefined,
          session_status: status,
          limit: size,
          offset: page * size,
        }),
        tradersService.getAuthSessionsSummary(),
      ]);
      setItems(sessionsData.items);
      setTotal(sessionsData.total);
      setSummary(summaryData);
    } catch (e: any) {
      setError(
        e.response?.data?.detail ||
          e.message ||
          'Не удалось загрузить информацию об устройствах',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [page, size, status]);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Устройства пользователей
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Контроль 2FA и полная история доверенных устройств пользователей.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={() => void load()}
          disabled={loading}
        >
          Обновить
        </Button>
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(auto-fit, minmax(190px, 1fr))"
        gap={2}
        mb={3}
      >
        {summaryCards.map(card => (
          <Paper key={card.key} variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {card.label}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {summary[card.key]}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <TextField
            label="Пользователь, email или IP"
            value={search}
            onChange={event => setSearch(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                setPage(0);
                void load();
              }
            }}
            sx={{ minWidth: 320, flex: 1 }}
          />
          <TextField
            select
            label="Статус"
            value={status}
            onChange={event => {
              setStatus(event.target.value as FilterStatus);
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">Все</MenuItem>
            <MenuItem value="active">Активные</MenuItem>
            <MenuItem value="expired">Истёкшие</MenuItem>
            <MenuItem value="revoked">Отозванные</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={() => {
              setPage(0);
              void load();
            }}
          >
            Найти
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Пользователь</TableCell>
                <TableCell>2FA</TableCell>
                <TableCell>Устройство</TableCell>
                <TableCell>IP / место</TableCell>
                <TableCell>Создано</TableCell>
                <TableCell>Последняя активность</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Отозвано</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(session => (
                <TableRow key={session.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {session.username}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {session.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={session.totp_enabled ? 'Включена' : 'Выключена'}
                      color={session.totp_enabled ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell title={session.user_agent}>
                    {session.browser} · {session.os}
                  </TableCell>
                  <TableCell>
                    {session.last_ip || '—'}
                    <br />
                    <Typography variant="caption" color="text.secondary">
                      {session.location || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(session.created_at)}</TableCell>
                  <TableCell>{formatDate(session.last_seen_at)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={labels[session.status]}
                      color={
                        session.status === 'active'
                          ? 'success'
                          : session.status === 'revoked'
                            ? 'error'
                            : 'warning'
                      }
                    />
                  </TableCell>
                  <TableCell>{formatDate(session.revoked_at)}</TableCell>
                </TableRow>
              ))}
              {!loading && !items.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    История устройств не найдена
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={size}
          onRowsPerPageChange={event => {
            setSize(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 250]}
          labelRowsPerPage="Строк на странице"
        />
      </Paper>
    </Box>
  );
}
